import s from './styles.module.scss'
import cn from 'clsx'
import { useEffect, useMemo, useState } from 'react'

const icons = import.meta.glob('@Static/images/market_icons/*.svg', { as: 'url' })

const iconIndex: Record<string, string> = Object.keys(icons).reduce((acc, key) => {
  const fileName = key.split('/').pop()
  if (fileName) acc[fileName] = key
  return acc
}, {} as Record<string, string>)

type Props = {
  source_currency: string
  target_currency?: string
  className?: string
  alt?: string
}

export const MarketIcon = ({ source_currency, target_currency, className, alt }: Props) => {
  const source = (source_currency ?? '').toLowerCase()
  const target = (target_currency ?? '').toLowerCase()

  const pairFile = useMemo(() => (source && target ? `${source}-${target}.svg` : null), [source, target])
  const singleFile = useMemo(() => (source ? `${source}.svg` : null), [source])
  const usdFallbackFile = useMemo(() => (source ? `${source}-usd.svg` : null), [source])

  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const tryLoad = async (fileName: string | null) => {
      if (!fileName) return null
      const key = iconIndex[fileName]
      if (!key) return null
      return await icons[key]()
    }

    const load = async () => {
      setUrl(null)

      const pairUrl = await tryLoad(pairFile)
      if (pairUrl && !cancelled) return setUrl(pairUrl)

      const singleUrl = await tryLoad(singleFile)
      if (singleUrl && !cancelled) return setUrl(singleUrl)

      const usdUrl = await tryLoad(usdFallbackFile)
      if (usdUrl && !cancelled) return setUrl(usdUrl)

      if (!cancelled) setUrl(null)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [pairFile, singleFile, usdFallbackFile])

  if (!url) return null

  return (
    <img
      className={cn(s.icon, className)}
      src={url}
      alt={alt ?? `${source_currency}${target_currency ? `/${target_currency}` : ''}`}
    />
  )
}
